import { useCallback, useState } from 'react'
import { Empty, ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { Dialog } from '../../components/Dialog'
import { ApiError, type TenantSummary, assumedIdentity, platform, session } from '../../lib/api'
import { slugProblem } from '../../lib/orgPath'

/** What a support session may be granted. Owner is absent, and that absence is the control. */
const SUPPORT_ROLES = ['viewer', 'analyst', 'administrator'] as const

/**
 * The organizations on this deployment, for whoever runs it.
 *
 * Only a platform administrator reaches this, which is nobody at any customer. The navigation hides
 * the whole group from everybody else, and the API refuses it regardless.
 *
 * There is no delete, and there deliberately never will be one here. Every table belonging to an
 * organization cascades from its row, including the byte-exact device bodies the product promises to
 * keep forever. Retiring stops an organization and keeps all of it, and can be undone.
 */
export function TenantListPage({ onAssumed }: { onAssumed: () => void }) {
  const roles = session.identity()?.roles ?? []
  if (!roles.includes('platform-admin')) {
    return (
      <div className="page-header">
        <div>
          <h1>Organizations</h1>
          <p>This screen belongs to whoever runs this deployment. Your account is not one of them.</p>
        </div>
      </div>
    )
  }

  return <PlatformTenantList onAssumed={onAssumed} />
}

function PlatformTenantList({ onAssumed }: { onAssumed: () => void }) {
  const [search, setSearch] = useState('')
  const [reloadToken, setReloadToken] = useState(0)
  const [adding, setAdding] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const reload = useCallback(() => setReloadToken((token) => token + 1), [])
  const tenants = useAsync(() => platform.listTenants(search || undefined), [search, reloadToken])

  /**
   * Shows the organization that was just created, by searching for it.
   *
   * Without this, creating one on a deployment with more than a screenful of customers appears to do
   * nothing: the list is ordered by name, so a new organization lands wherever its name falls, which
   * is usually not the first page. Being shown the thing you just made is the least a screen can do,
   * and it is also the answer to "did that work".
   *
   * The sentence the dialog used to end on comes back with it and is shown on the page, because the
   * dialog it was written in is gone by the time anybody could read it - and one of the two things it
   * can say is that the organization has no signing key and cannot be served policy.
   */
  const showCreated = useCallback((slug: string, created: string) => {
    setSearch(slug)
    setNote(created)
    setAdding(false)
    reload()
  }, [reload])

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Organizations</h1>
          <p>Everyone who uses this deployment, and the state each of them is in.</p>
        </div>
        <button type="button" className="primary" onClick={() => setAdding(true)} data-testid="add-tenant">
          New organization
        </button>
      </div>

      <ErrorBanner message={tenants.error} />

      {/*
        What just happened, in words, on the page rather than in the dialog that caused it. The list
        below is filtered to the new organization, and an operator who did not notice the search box
        change would be reading a one-row table wondering where their customers went.
      */}
      {note && (
        <div className="banner success" role="status" data-testid="create-tenant-note">
          {note}
        </div>
      )}

      <CreateTenantDialog open={adding} onClose={() => setAdding(false)} onCreated={showCreated} />

      <div className="card">
        <div className="card-body">
          <label htmlFor="tenant-search">Search</label>
          <input
            id="tenant-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or identifier"
          />

          {/*
            The count, and whether what is on the screen is all of it.

            A list capped at a page with nothing saying so reads as the whole customer base, which on
            this screen of all screens is the wrong thing to believe. Stating it is the same rule the
            rest of the console follows: a number that was not shown is never implied.
          */}
          {tenants.value && (
            <p className="muted" data-testid="tenant-count">
              {tenants.value.total === tenants.value.items.length
                ? `${tenants.value.total} organizations.`
                : `Showing ${tenants.value.items.length} of ${tenants.value.total}. Search to narrow it.`}
            </p>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table data-testid="tenant-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Status</th>
                  <th>Accounts</th>
                  <th>Devices</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tenants.value?.items.map((tenant) => (
                  <TenantRow
                    key={tenant.tenantId}
                    tenant={tenant}
                    deletionEnabled={tenants.value?.deletionEnabled ?? false}
                    onChanged={reload}
                    onAssumed={onAssumed}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {tenants.value && tenants.value.items.length === 0 && (
            <Empty>{tenants.loading ? 'Loading…' : 'No organizations yet.'}</Empty>
          )}
        </div>
      </div>
    </>
  )
}

/** What the row is currently asking for, if anything. Only one thing at a time. */
type Asking = { kind: 'status'; status: string } | { kind: 'assume' } | { kind: 'delete' } | null

function TenantRow({
  tenant,
  deletionEnabled,
  onChanged,
  onAssumed
}: {
  tenant: TenantSummary
  deletionEnabled: boolean
  onChanged: () => void
  onAssumed: () => void
}) {
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState<Asking>(null)
  const [reason, setReason] = useState('')
  // Deletion asks for the identifier to be retyped rather than for a reason: there will be no row
  // left to keep a reason on, and the point of the field is to make sure this is the organization
  // somebody meant.
  const [confirmSlug, setConfirmSlug] = useState('')
  // Revealed only once the server has refused and said how many bodies are at stake, so what is
  // being agreed to is a number somebody has actually been shown.
  const [acceptDataLoss, setAcceptDataLoss] = useState(false)
  const [bodiesAtRisk, setBodiesAtRisk] = useState<number | null>(null)
  // Viewer by default, because the commonest reason to open somebody's organization is to look at
  // something they have described, and the least authority that answers the question is the right
  // one to take.
  const [supportRole, setSupportRole] = useState<string>('viewer')

  const ask = (next: Asking) => {
    setAsking(next)
    setReason('')
    setConfirmSlug('')
    setAcceptDataLoss(false)
    setBodiesAtRisk(null)
    setProblem(null)
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!asking) return

    setBusy(true)
    setProblem(null)
    try {
      if (asking.kind === 'status') {
        await platform.setTenantStatus(tenant.tenantId, asking.status, reason)
        setAsking(null)
        onChanged()
        return
      }

      if (asking.kind === 'delete') {
        await platform.deleteTenant(tenant.tenantId, confirmSlug, acceptDataLoss)
        setAsking(null)
        onChanged()
        return
      }

      const assumed = await platform.assume(tenant.tenantId, supportRole, reason)
      // Parks the platform session rather than replacing it, so an assumed token expiring does not
      // sign the administrator out of the platform as well.
      session.assume(
        assumedIdentity(assumed, session.identity()?.displayName ?? 'a platform administrator')
      )
      onAssumed()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'That could not be done.')
      // A refused deletion carries what it would have destroyed. Keeping the count turns the second
      // attempt into a specific agreement rather than a repeated click.
      if (error instanceof ApiError && error.tenantDeletion) setBodiesAtRisk(error.tenantDeletion.bodies)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <tr data-testid={`tenant-${tenant.slug}`}>
        <td>
          {tenant.name}
          <br />
          <span className="muted">{tenant.slug}</span>
          {/*
            An organization with no signing key is served no policy at all, and the first symptom is
            a device whose feed is silently empty. Provisioning has always reported this and nothing
            has ever shown it afterwards, so it is said here in words rather than left to be found.
          */}
          {!tenant.hasSigningKey && (
            <p className="error" data-testid="no-signing-key">
              No signing key, so no device can be served policy.
            </p>
          )}
        </td>
        <td data-testid={`status-${tenant.slug}`}>{tenant.status}</td>
        <td>{tenant.accounts}</td>
        <td>{tenant.devices}</td>
        <td>{formatUtc(tenant.createdUtc)}</td>
        <td>
          {/* Owner is not offered, and the server refuses it if it is asked for anyway. A support
              session that could manage a customer's accounts is not support. */}
          <select
            value={supportRole}
            disabled={busy}
            aria-label={`Open ${tenant.name} as`}
            onChange={(event) => setSupportRole(event.target.value)}
          >
            {SUPPORT_ROLES.map((role) => (
              <option key={role} value={role}>
                as {role}
              </option>
            ))}
          </select>{' '}
          <button
            type="button"
            onClick={() => ask({ kind: 'assume' })}
            disabled={busy}
            data-testid={`assume-${tenant.slug}`}
          >
            Open
          </button>{' '}
          {tenant.status === 'active' ? (
            <>
              <button
                type="button"
                onClick={() => ask({ kind: 'status', status: 'suspended' })}
                disabled={busy}
                data-testid={`suspend-${tenant.slug}`}
              >
                Suspend
              </button>{' '}
              <button
                type="button"
                onClick={() => ask({ kind: 'status', status: 'retired' })}
                disabled={busy}
                data-testid={`retire-${tenant.slug}`}
              >
                Retire
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => ask({ kind: 'status', status: 'active' })}
                disabled={busy}
                data-testid={`reactivate-${tenant.slug}`}
              >
                Reactivate
              </button>
              {/* Offered only for an organization that is already retired, and only where the
                  deployment turned deletion on. Retirement is the staging area for the one thing
                  here that cannot be undone. */}
              {deletionEnabled && tenant.status === 'retired' && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="danger"
                    onClick={() => ask({ kind: 'delete' })}
                    disabled={busy}
                    data-testid={`delete-${tenant.slug}`}
                  >
                    Delete
                  </button>
                </>
              )}
            </>
          )}
        </td>
      </tr>

      {/*
        Asked inline rather than in a browser dialog. The reason is written for the customer to read
        later, and a prompt box is a poor place to compose a sentence somebody else will be shown -
        it cannot be corrected, it cannot say who will see it, and it blocks the page it belongs to.
      */}
      {asking && (
        <tr data-testid={`reason-row-${tenant.slug}`}>
          <td colSpan={6}>
            <form onSubmit={submit}>
              {asking.kind === 'delete' ? (
                <>
                  <label htmlFor={`confirm-slug-${tenant.slug}`}>
                    Deleting {tenant.name} cannot be undone. Type its organization ID,{' '}
                    <strong>{tenant.slug}</strong>, to confirm.
                  </label>
                  <p className="hint">
                    Everything it owns goes with it: its people, its policies, its devices and
                    anything they reported. Retiring it instead keeps all of that and is reversible.
                  </p>
                  <input
                    id={`confirm-slug-${tenant.slug}`}
                    value={confirmSlug}
                    onChange={(event) => setConfirmSlug(event.target.value)}
                    data-testid={`confirm-slug-${tenant.slug}`}
                    autoComplete="off"
                    required
                  />
                  {/* Appears only after the server has refused and said how many. Agreeing to a
                      number nobody has been shown is not agreement. */}
                  {bodiesAtRisk !== null && bodiesAtRisk > 0 && (
                    <p className="hint">
                      <label>
                        <input
                          type="checkbox"
                          checked={acceptDataLoss}
                          onChange={(event) => setAcceptDataLoss(event.target.checked)}
                          data-testid={`accept-data-loss-${tenant.slug}`}
                        />{' '}
                        Destroy the {bodiesAtRisk} device {bodiesAtRisk === 1 ? 'body' : 'bodies'} it
                        holds. Nothing else has a copy of them.
                      </label>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <label htmlFor={`reason-${tenant.slug}`}>
                    {asking.kind === 'assume'
                      ? `Why do you need to open ${tenant.name}? They can read this, so write it for them.`
                      : `Why is ${tenant.name} becoming ${asking.status}? This is kept, and their owner can read it.`}
                  </label>
                  <input
                    id={`reason-${tenant.slug}`}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    data-testid={`reason-${tenant.slug}`}
                    required
                  />
                </>
              )}
              {problem && (
                <p className="error" role="alert" data-testid="tenant-problem">
                  {problem}
                </p>
              )}
              <button
                type="submit"
                className={asking.kind === 'delete' ? 'danger' : undefined}
                disabled={busy || (asking.kind === 'delete' && bodiesAtRisk !== null && bodiesAtRisk > 0 && !acceptDataLoss)}
                data-testid={`confirm-${tenant.slug}`}
              >
                {asking.kind === 'assume'
                  ? 'Open organization'
                  : asking.kind === 'delete'
                    ? 'Delete permanently'
                    : `Mark ${asking.status}`}
              </button>{' '}
              <button type="button" onClick={() => setAsking(null)} disabled={busy}>
                Cancel
              </button>
            </form>
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * Everything an organization needs to exist, asked for in a dialog rather than on the page.
 *
 * This screen is a list of customers and the states they are in; a five-field form for the thing an
 * operator does least often was the first thing on it. A refusal keeps the dialog open, for the same
 * reason `CreateAccountDialog` does - the rules live on the server, so the first anybody hears of a
 * name already taken is a round trip, and closing over five filled-in fields would mean typing them
 * all again.
 */
function CreateTenantDialog({
  open,
  onClose,
  onCreated
}: {
  open: boolean
  onClose: () => void
  onCreated: (slug: string, note: string) => void
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [ownerPassword, setOwnerPassword] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const slugIssue = slug === '' ? null : slugProblem(slug)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (slugProblem(slug)) return
    setSaving(true)
    setProblem(null)
    try {
      const created = await platform.createTenant(name, slug, ownerEmail, ownerName || ownerEmail, ownerPassword)
      // Reported rather than assumed. A deployment with no key-encryption key configured mints no
      // key, and an organization that silently could not be served policy is the worst way to find
      // that out. Handed to the page, which is still there once this dialog has closed.
      const note = created.signingKeyId
        ? `${name} is ready. Its owner signs in with the identifier "${slug}".`
        : `${name} was created, but this deployment has no key-encryption key, so it has no signing ` +
          'key and cannot be served policy until one is configured.'
      const created_slug = slug
      setName('')
      setSlug('')
      setOwnerEmail('')
      setOwnerName('')
      setOwnerPassword('')
      onCreated(created_slug, note)
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The organization could not be created.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} title="New organization" onClose={onClose} data-testid="create-tenant-dialog">
      <p className="muted" style={{ marginTop: 0 }}>
        Creates the organization, its first owner, its signing key, and the dictionaries the product
        ships with. The owner chooses everything after that.
      </p>
      <form onSubmit={submit} data-testid="create-tenant-form">
        {/* Four of these five fields are a name or an address of some kind, and which one somebody
            types to sign in was the question this form kept provoking. Each label now says what the
            value is for rather than what it is, and the hints stay visible while the field is being
            filled in - a placeholder answers the question only until you start typing. */}
        <label htmlFor="tenant-name">Organization name</label>
        <p className="hint" id="tenant-name-hint">
          The full name, shown throughout their console. For example, Acme Corporation.
        </p>
        <input
          id="tenant-name"
          aria-describedby="tenant-name-hint"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />

        <label htmlFor="tenant-slug">Organization ID</label>
        <p className="hint" id="tenant-slug-hint">
          What their people type to sign in, and the start of every address in their console. 2 to 63
          lower-case letters, digits and hyphens, not starting or ending with a hyphen &mdash; for
          example, acme or acme-emea. It cannot be changed later.
        </p>
        <input
          id="tenant-slug"
          aria-describedby="tenant-slug-hint tenant-slug-check"
          aria-invalid={slugIssue !== null}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={slug}
          // Lower-cased as typed rather than refused, because capitals are the one mistake with an
          // obvious correction and the server stores it lower-cased regardless. Everything else is
          // shown and left for the person to fix, since guessing what they meant is worse than saying.
          onChange={(event) => setSlug(event.target.value.toLowerCase())}
          required
        />
        {/* Checked as it is typed, against the same rule the server applies, so what is wrong is said
            beside the field rather than after a round trip. When the ID is good, this shows the
            address the owner will actually be given, which is the thing worth checking by eye. */}
        {slug !== '' && (
          <p
            className={slugIssue ? 'error' : 'hint'}
            id="tenant-slug-check"
            data-testid="tenant-slug-check"
            aria-live="polite"
          >
            {slugIssue ?? (
              <>
                They will sign in at{' '}
                <strong>
                  {window.location.origin}/{slug}/sign-in
                </strong>
              </>
            )}
          </p>
        )}

        <label htmlFor="owner-email">Owner&rsquo;s email address</label>
        <p className="hint" id="owner-email-hint">Their sign-in name, alongside the organization ID above.</p>
        <input
          id="owner-email"
          type="email"
          aria-describedby="owner-email-hint"
          value={ownerEmail}
          onChange={(event) => setOwnerEmail(event.target.value)}
          required
        />

        <label htmlFor="owner-name">Owner&rsquo;s full name</label>
        <p className="hint" id="owner-name-hint">Shown against anything they do. Optional.</p>
        <input
          id="owner-name"
          aria-describedby="owner-name-hint"
          value={ownerName}
          onChange={(event) => setOwnerName(event.target.value)}
        />

        <label htmlFor="owner-password">Owner&rsquo;s first password</label>
        <p className="hint" id="owner-password-hint">
          You tell them what it is. They have to replace it before they can use the console.
        </p>
        <input
          id="owner-password"
          type="text"
          aria-describedby="owner-password-hint"
          value={ownerPassword}
          onChange={(event) => setOwnerPassword(event.target.value)}
          required
        />

        {/* Inside the dialog, beside the fields it is about. A refusal rendered on the page behind
            would be hidden by the thing that caused it. */}
        {problem && (
          <p className="error" role="alert" data-testid="create-tenant-error">
            {problem}
          </p>
        )}

        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={saving || slugIssue !== null}>
            {saving ? 'Creating…' : 'Create organization'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
